import { ImageEditor } from "@/components/ui";
import env from "@/env.config";
import useBackward from "@/hooks/useBackward";
import useGallery from "@/hooks/useGallery";
import { DataIdSchema } from "@/schemas";
import { useGalleryStore } from "@/stores";
import Drawer from "@mui/material/Drawer";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/gallery/view/$id")({
	params: {
		parse: (params) => ({
			id: DataIdSchema.parse(Number(params.id)),
		}),
		stringify: ({ id }) => ({ id: id.toString() }),
	},
	beforeLoad: ({ params }) => {
		const { images } = useGalleryStore.getState();
		if (!images[params.id]) {
			throw redirect({
				to: "/dashboard/gallery/view",
			});
		}
	},
	loader: ({ params }) => useGalleryStore.getState().findImage(+params.id),
	component: RouteComponent,
});

function RouteComponent() {
	const onBack = useBackward();
	const fileIdx = Route.useParams({
		select: (params) => params.id,
	});
	const navigate = Route.useNavigate();
	const file = Route.useLoaderData();
	const { onFinishEdit } = useGallery();

	const onCloseDrawer = (
		e: React.MouseEvent<HTMLElement>,
		_reason?: "backdropClick" | "escapeKeyDown",
	) => {
		e?.stopPropagation();
		e?.preventDefault();
		return navigate({
			to: "/dashboard/gallery/view",
		});
	};

	const onClickDone = async (
		e: React.MouseEvent<HTMLButtonElement>,
		imageStr: string,
	) => {
		e.preventDefault();
		try {
			await onFinishEdit(imageStr, fileIdx);
			return onBack();
		} catch (err) {
			toast.error("Failed to save image");
		}
	};

	return (
		<Drawer
			anchor="bottom"
			open={true}
			onClose={onCloseDrawer}
			disableEnforceFocus
		>
			<div className="relative wrapper h-dvh w-full">
				<ImageEditor
					includeUI={{
						loadImage: {
							path: URL.createObjectURL(file),
							name: file.name,
						},
						uiSize: {
							width: "auto",
							height: "100vh",
						},
					}}
					usageStatistics={!env.isProd}
					onDone={onClickDone}
					onClose={onCloseDrawer}
					className="overflow-hidden"
				/>
			</div>
		</Drawer>
	);
}
